import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { brl, fdatetime, todayISO, daysAgoISO } from "@/lib/format";
import StatCard from "@/components/StatCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Download, Printer, TrendingUp, TrendingDown, Wallet, Receipt } from "lucide-react";

const PRESETS = [
  { label: "Hoje", days: 0 },
  { label: "7 dias", days: 6 },
  { label: "30 dias", days: 29 },
];

export default function Relatorios() {
  const [start, setStart] = useState(daysAgoISO(6));
  const [end, setEnd] = useState(todayISO());
  const [kind, setKind] = useState("");
  const [pmId, setPmId] = useState("");
  const [sellerId, setSellerId] = useState("");

  const { data: sellers } = useQuery({
    queryKey: ["sellers"],
    queryFn: async () => (await api.get("/sellers")).data,
  });

  const { data: pms } = useQuery({
    queryKey: ["payment-methods"],
    queryFn: async () => (await api.get("/payment-methods")).data,
  });
  const { data, isLoading } = useQuery({
    queryKey: ["report", start, end, kind, pmId, sellerId],
    queryFn: async () =>
      (await api.get("/reports/summary", { params: { start, end, kind, payment_method_id: pmId, seller_id: sellerId } })).data,
    enabled: !!start && !!end,
  });

  const rows = useMemo(() => {
    if (!data) return [];
    const r = [
      ...data.sales.map((s) => ({
        id: s.id, date: s.created_at, tipo: "Venda",
        desc: s.items.map((i) => `${i.qty > 1 ? i.qty + "x " : ""}${i.name}`).join(", "),
        aluno: s.student_name || "Avulsa", pagamento: s.payment_method_name,
        vendedor: s.seller_name || "—",
        valor: s.total, resp: s.user_name, positive: true,
      })),
      ...data.movements.map((m) => ({
        id: m.id, date: m.created_at, tipo: m.type === "entrada" ? "Entrada" : "Saída",
        desc: `${m.category}${m.description ? " — " + m.description : ""}`,
        aluno: "—", pagamento: m.payment_method_name, vendedor: "—",
        valor: m.amount, resp: m.user_name, positive: m.type === "entrada",
      })),
    ];
    return r.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [data]);

  const exportCsv = async () => {
    const r = await api.get("/reports/export", {
      params: { start, end, kind, payment_method_id: pmId, seller_id: sellerId },
      responseType: "blob",
    });
    const url = URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `relatorio_${start}_${end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="relatorios-page">
      <div className="no-print flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Relatórios</h1>
          <p className="text-sm text-muted-foreground">Filtros avançados e exportação</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
            <Download className="mr-1 h-4 w-4" /> Exportar CSV
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-btn">
            <Printer className="mr-1 h-4 w-4" /> Imprimir / PDF
          </Button>
        </div>
      </div>

      <div className="no-print flex flex-wrap items-end gap-3">
        {PRESETS.map((p) => (
          <Button
            key={p.label}
            variant="outline"
            size="sm"
            data-testid={`preset-${p.label.replace(" ", "-")}`}
            onClick={() => { setStart(daysAgoISO(p.days)); setEnd(todayISO()); }}
          >
            {p.label}
          </Button>
        ))}
        <div className="space-y-1">
          <Label className="text-xs">De</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="report-start" className="w-40" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Até</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="report-end" className="w-40" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Tipo</Label>
          <Select value={kind || "todos"} onValueChange={(v) => setKind(v === "todos" ? "" : v)}>
            <SelectTrigger data-testid="report-kind" className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="venda">Vendas</SelectItem>
              <SelectItem value="entrada">Entradas</SelectItem>
              <SelectItem value="saida">Saídas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Pagamento</Label>
          <Select value={pmId || "todas"} onValueChange={(v) => setPmId(v === "todas" ? "" : v)}>
            <SelectTrigger data-testid="report-pm" className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas</SelectItem>
              {(pms || []).map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Vendedor</Label>
          <Select value={sellerId || "todos"} onValueChange={(v) => setSellerId(v === "todos" ? "" : v)}>
            <SelectTrigger data-testid="report-seller" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              {(sellers || []).map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading || !data ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}</div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard testid="report-entradas" title="Entradas" value={data.totals.entradas} icon={TrendingUp} tone="success" />
            <StatCard testid="report-saidas" title="Saídas" value={data.totals.saidas} icon={TrendingDown} tone="danger" />
            <StatCard testid="report-saldo" title="Saldo do período" value={data.totals.saldo} icon={Wallet} />
            <StatCard testid="report-ticket" title="Ticket médio" value={data.totals.ticket_medio} icon={Receipt}
              sub={`${data.totals.count} vendas`} />
          </div>

          {data.by_method.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="report-by-method">
              {data.by_method.map((m) => (
                <Badge key={m.name} variant="outline" className="px-3 py-1.5">
                  {m.name}: <span className="num ml-1 font-semibold">{brl(m.value)}</span>
                </Badge>
              ))}
            </div>
          )}

          {(data.by_seller || []).length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="report-by-seller">
              {data.by_seller.map((s) => (
                <Badge key={s.name} variant="outline" className="border-emerald-500/40 text-emerald-500 px-3 py-1.5">
                  {s.name}: <span className="num ml-1 font-semibold">{brl(s.total)}</span>
                  <span className="ml-1 text-muted-foreground">({s.count} vendas)</span>
                </Badge>
              ))}
            </div>
          )}

          <Card>
            <CardContent className="overflow-x-auto p-0">
              <Table data-testid="report-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Aluno</TableHead>
                    <TableHead>Pagamento</TableHead>
                    <TableHead>Vendedor</TableHead>
                    <TableHead>Responsável</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={`${r.tipo}-${r.id}`} data-testid={`report-row-${r.id}`}>
                      <TableCell className="whitespace-nowrap">{fdatetime(r.date)}</TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={r.tipo === "Saída" ? "border-red-500/40 text-red-500" : "border-emerald-500/40 text-emerald-500"}
                        >
                          {r.tipo}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-72 truncate">{r.desc}</TableCell>
                      <TableCell className="text-muted-foreground">{r.aluno}</TableCell>
                      <TableCell className="text-muted-foreground">{r.pagamento}</TableCell>
                      <TableCell className="font-medium">{r.vendedor}</TableCell>
                      <TableCell className="text-muted-foreground">{r.resp}</TableCell>
                      <TableCell className={cn("num text-right font-semibold", r.positive ? "text-emerald-500" : "text-red-500")}>
                        {r.positive ? "+" : "−"}{brl(r.valor)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground">
                        Nenhum lançamento no período com os filtros atuais.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
